/**
 * useAutosave
 * Debounced autosave hook for the Director Notes textarea.
 *
 * Usage:
 *   const { status, lastSavedAt, triggerSave } = useAutosave({
 *     value,
 *     onSave: async (v) => { await mutation.mutateAsync(v); },
 *     delay: 1500,
 *   });
 *
 * Status values:
 *   "idle"    — no pending save, nothing has been typed yet
 *   "dirty"   — user has typed, debounce timer is running
 *   "saving"  — async save call in flight
 *   "saved"   — last save succeeded
 *   "error"   — last save failed
 */

import { useState, useEffect, useRef, useCallback } from "react";

export type AutosaveStatus = "idle" | "dirty" | "saving" | "saved" | "error";

interface UseAutosaveOptions {
  /** Current textarea value to watch */
  value: string;
  /** Async function that performs the actual save */
  onSave: (value: string) => Promise<void>;
  /** Debounce delay in ms (default 1500) */
  delay?: number;
  /** Whether autosave is enabled (default true) */
  enabled?: boolean;
}

interface UseAutosaveReturn {
  status: AutosaveStatus;
  /** ISO string of the last successful server-confirmed save */
  lastSavedAt: string | null;
  /** Force an immediate save (bypasses debounce) */
  triggerSave: () => void;
}

export function useAutosave({
  value,
  onSave,
  delay = 1500,
  enabled = true,
}: UseAutosaveOptions): UseAutosaveReturn {
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // Track whether the value has been touched at all (skip initial mount)
  const isFirstRender = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValue = useRef(value);
  latestValue.current = value;

  const executeSave = useCallback(async () => {
    if (!enabled) return;
    setStatus("saving");
    try {
      await onSave(latestValue.current);
      setLastSavedAt(new Date().toISOString());
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }, [onSave, enabled]);

  // Debounce: whenever value changes (after mount), reset the timer
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!enabled) return;

    setStatus("dirty");

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      executeSave();
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delay, enabled, executeSave]);

  const triggerSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    executeSave();
  }, [executeSave]);

  return { status, lastSavedAt, triggerSave };
}
